import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IRaffleBlacklist } from "../../../../interfaces";
import useAuth from "../../../../hooks/useAuth";
import { SubmitHandler, useForm } from "react-hook-form";
import { EmitError } from "../../../../utils/EmitError";
import { AxiosError } from "axios";
import { EmitSuccessAlert } from "../../../../utils/EmitSuccessAlert";
import RaffleBlacklistService from "../../../../services/http/services/RaffleBlacklistService";
import { formatCpfCnpj } from "../../../../utils/Masks";
import { useGetColumnSearchProps } from "../../../../hooks/FilterTable/stringClearFiltros";
interface ITableData extends IRaffleBlacklist {
    key: number | undefined;
}


export default function useRaffleBlacklist() {
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { activeCity } = useAuth();
    const [modalVisible, setModalVisible] = useState(false);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
    const [showTable, setShowTable] = useState<boolean>(true);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [cpfCnpjBeingDeleted, setCpfCnpjBeingDeleted] = useState<ITableData | null>(null);
    const [dataSelected, setDataSelected] = useState<IRaffleBlacklist | null>(null);
    const refFuncaoLimparFiltrosString = useRef<any>(null);

    const {
        handleSubmit,
        formState: { errors },
        control,
        setError,
        reset
    } = useForm<IRaffleBlacklist>({
        defaultValues: {
            cpfCnpj: '',
            name: '',
        },
        shouldFocusError: false
    });


    const handleOpenModal = useCallback(() => {
        setModalVisible((prev) => !prev);
    }, []);

    const handleShowTable = useCallback(() => {
        setShowTable((prev) => !prev);
        setSelectedIds([]);
    }, []);

    const handleClickEdit = useCallback(() => {
        setShowTable(false);

        const controller = new AbortController();

        async function loadCnpjBlacklistById() {
            try {
                setIsLoading(true);

                const cnpjBlacklist = await RaffleBlacklistService.getBlacklistById(selectedIds[0]!, controller.signal);
                setDataSelected(cnpjBlacklist);

                reset({
                    cpfCnpj: formatCpfCnpj(cnpjBlacklist.cpfCnpj),
                    name: cnpjBlacklist.name,
                });


                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'cnpjBlacklist_aosdpasdpasd');
                setIsLoading(false);
            }
        }

        loadCnpjBlacklistById();
    }, [selectedIds, reset]);

    const onClickTableRow = useCallback((record: any, index: any) => {
        if (selectedIds.includes(record.id)) {
            setSelectedIds((prevState) => prevState.filter((id) => id !== record.id));
        } else {
            setSelectedIds((prevState) => [...prevState, record.id]);
        }
    }, [selectedIds]);

    const onSelectChange = useCallback((newSelectedRowKeys: Array<any>) => {
        if (newSelectedRowKeys.length > selectedIds.length) {
            if (newSelectedRowKeys.length - 1 === selectedIds.length) {
                const newId = newSelectedRowKeys.pop();
                return setSelectedIds((prevState) => [...prevState, newId]);
            }

            if (newSelectedRowKeys.length - 1 > selectedIds.length) {
                return setSelectedIds(newSelectedRowKeys);
            }
        }

        setSelectedIds(newSelectedRowKeys);
    }, [selectedIds]);

    const rowSelection = useMemo(() => ({
        type: 'checkbox' as const,
        selectedRowKeys: selectedIds,
        onChange: onSelectChange,
    }), [selectedIds, onSelectChange]);

    const columns = [
        {
            title: 'Nome',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: IRaffleBlacklist, b: IRaffleBlacklist) => a.name?.localeCompare(b.name || '')!,
            showSorterTooltip: false,
            ...useGetColumnSearchProps({dataIndex: 'name', ref: refFuncaoLimparFiltrosString}),
        },
        {
            title: 'CPF/CNPJ',
            dataIndex: 'cpfCnpj',
            key: 'cpfCnpj',
            sorter: (a: IRaffleBlacklist, b: IRaffleBlacklist) => a.cpfCnpj?.localeCompare(b.cpfCnpj || '')!,
            showSorterTooltip: false,
            ...useGetColumnSearchProps({dataIndex: 'cpfCnpj', ref: refFuncaoLimparFiltrosString, formatter: formatCpfCnpj}),
        },
    ]

    const setTableDataFromRequest = useCallback((data: Array<IRaffleBlacklist>) => {
        const newData = data.map((cnpjBlacklist) => ({ ...cnpjBlacklist, key: cnpjBlacklist.id }));
        setTableData(newData);
    }, []);

    const loadCnpjBlacklist = useCallback(async () => {
        try {
            const controller = new AbortController();

            const blacklist = await RaffleBlacklistService.listBlacklist(activeCity?.id!, controller.signal);
            setTableDataFromRequest(blacklist);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }

            EmitError(err, true, 'cnpjBlacklist_aosdpasdpasd');
        }
    }, [activeCity?.id, setTableDataFromRequest]);



    const removeMaskCpfCnpj = useCallback((cnpj: string) => {
        return cnpj.replace(/\D/g, '');
    }, [])

    const handleSubmitForm: SubmitHandler<IRaffleBlacklist> = useCallback(async (data): Promise<boolean> => {
        try {
            setIsSubmiting(true);

            if (!activeCity || !activeCity.id) {
                return false;
            }

            if (!data.cpfCnpj) {
                setError('cpfCnpj', {
                    message: 'Informe o CPF/CNPJ'
                });
                return false;
            }

            data.cpfCnpj = removeMaskCpfCnpj(data.cpfCnpj);

            if (!dataSelected) {
                await RaffleBlacklistService.insertInBlacklist({ cpfCnpj: data.cpfCnpj, name: data.name, countyId: activeCity.id });
                EmitSuccessAlert("Sucesso!", "CPF/CNPJ cadastrado com sucesso!");
            }

            if (dataSelected) {
                if (data.cpfCnpj.length !== 11 && data.cpfCnpj.length !== 14) {
                    setError('cpfCnpj', {
                    message: 'CPF/CNPJ inválido'
                });
                return false;
                }

                await RaffleBlacklistService.updateInBlacklist({ id: dataSelected.id, cpfCnpj: data });
                EmitSuccessAlert("Sucesso!", "CPF/CNPJ atualizado com sucesso!");
            }

            setShowTable(true);
            setSelectedIds([]);

            //Limpar os campos
            reset();

            loadCnpjBlacklist();

            return true;
        } catch (err) {
            EmitError(err);
            return false;
        } finally {
            setIsSubmiting(false);
        }
    }, [activeCity, dataSelected, loadCnpjBlacklist, removeMaskCpfCnpj, reset, setError]);

    const handleDeleteCnpjInBlacklist = useCallback((id: number) => {
        setIsDeleteModalVisible(true);
        setCpfCnpjBeingDeleted(tableData.find((element) => element.id === id) || null);
    }, [tableData]);

    const handleConfirmDeleteCnpjInBlacklist = useCallback(async () => {
        try {
            setIsLoadingDelete(true);
            await RaffleBlacklistService.deleteInBlacklist(cpfCnpjBeingDeleted?.id!);
            EmitSuccessAlert('CPF/CNPJ removido da lista de excluídos com sucesso!');
            setTableData((prevState) => prevState.filter((element) => element.id !== cpfCnpjBeingDeleted?.id));
            setIsDeleteModalVisible(false);
            setShowTable(true);
            setDataSelected(null);
            setCpfCnpjBeingDeleted(null);
            setSelectedIds([]);
            setIsLoadingDelete(false);
        } catch (err) {
            EmitError(err, true, 'cnpjBlacklist_poasdapenmasd');
            setIsLoadingDelete(false);
        }
    }, [cpfCnpjBeingDeleted]);

    const handleCancelDeleteCnpjInBlacklist = useCallback(async () => {
        setIsDeleteModalVisible(false);
        setCpfCnpjBeingDeleted(null);
    }, []);

    useEffect(() => {
        loadCnpjBlacklist();
    }, [loadCnpjBlacklist]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadCnpjBlacklist() {
            try {
                setIsLoading(true);

                const cnpjBlacklist = await RaffleBlacklistService.listBlacklist(activeCity?.id!, controller.signal);
                setTableDataFromRequest(cnpjBlacklist);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'cnpjBlacklist_aosdpasdpasd');
                setIsLoading(false);
            }
        }

        loadCnpjBlacklist();

        return () => {
            controller.abort();
        }
    }, [activeCity, setTableDataFromRequest]);

    return {
        modalVisible,
        handleOpenModal,
        columns,
        tableData,
        rowSelection,
        isLoading,
        onClickTableRow,
        handleSubmitForm,
        handleSubmit,
        errors,
        control,
        isSubmiting,
        selectedIds,
        showTable,
        handleShowTable,
        handleDeleteCnpjInBlacklist,
        handleConfirmDeleteCnpjInBlacklist,
        handleCancelDeleteCnpjInBlacklist,
        cpfCnpjBeingDeleted,
        isDeleteModalVisible,
        isLoadingDelete,
        handleClickEdit
    }
}
